import { Plus, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VisibilitySelect, type CapabilityVisibility } from '@/components/ui/visibility-select';
import { getCapabilityColors } from '@/lib/capability-colors';

export interface AgentBasicsFormValues {
    name: string;
    key: string;
    description: string;
    tags: string[];
    visibility: CapabilityVisibility;
}

interface AgentBasicsStepProps {
    values: AgentBasicsFormValues;
    errors: Record<string, string>;
    tagInput: string;
    onValuesChange: (values: Partial<AgentBasicsFormValues>) => void;
    onTagInputChange: (value: string) => void;
    onAddTag: () => void;
    onRemoveTag: (tag: string) => void;
    onTagKeyDown: (e: React.KeyboardEvent) => void;
    slugifyToKey: (value: string) => string;
}

const inputClassName =
    'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
const errorInputClassName =
    'w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

const colors = getCapabilityColors('agent');

export function AgentBasicsStep({
    values,
    errors,
    tagInput,
    onValuesChange,
    onTagInputChange,
    onAddTag,
    onRemoveTag,
    onTagKeyDown,
    slugifyToKey,
}: AgentBasicsStepProps) {
    const handleNameChange = (value: string) => {
        onValuesChange({ name: value, key: slugifyToKey(value) });
    };

    return (
        <div className="w-full space-y-6">
            <div className="space-y-2">
                <Label>
                    Agent Name <span className="text-rose-500">*</span>
                </Label>
                <Input
                    placeholder="e.g. Document Analyzer"
                    value={values.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className={errors.name ? errorInputClassName : inputClassName}
                    autoFocus
                />
                {errors.name && (
                    <p className="text-xs text-red-500">{errors.name}</p>
                )}
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
                <div className="space-y-2">
                    <Label>
                        Key <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                        placeholder="e.g. document_analyzer"
                        value={values.key}
                        onChange={(e) =>
                            onValuesChange({
                                key: slugifyToKey(e.target.value),
                            })
                        }
                        className={`font-mono text-xs ${errors.key ? errorInputClassName : inputClassName}`}
                    />
                    <p className="text-xs text-slate-400">
                        Unique identifier for the agent. Auto-generated from name.
                    </p>
                    {errors.key && (
                        <p className="text-xs text-red-500">{errors.key}</p>
                    )}
                </div>

                <VisibilitySelect
                    value={values.visibility}
                    onChange={(v) => onValuesChange({ visibility: v })}
                    className={inputClassName}
                />
            </div>

            <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                    placeholder="Brief description of what this agent does..."
                    value={values.description}
                    onChange={(e) =>
                        onValuesChange({ description: e.target.value })
                    }
                    rows={3}
                    className={`${inputClassName} resize-none`}
                />
            </div>

            <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                    <Input
                        placeholder="Add a tag..."
                        value={tagInput}
                        onChange={(e) => onTagInputChange(e.target.value)}
                        onKeyDown={onTagKeyDown}
                        className={inputClassName}
                    />
                    <button
                        type="button"
                        onClick={onAddTag}
                        disabled={!tagInput.trim()}
                        className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Plus size={14} />
                    </button>
                </div>
                {values.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {values.tags.map((tag) => (
                            <span
                                key={tag}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bgLight} ${colors.text}`}
                            >
                                {tag}
                                <button
                                    type="button"
                                    onClick={() => onRemoveTag(tag)}
                                    className="hover:opacity-70"
                                    aria-label={`Remove tag ${tag}`}
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
